<template>
  <div>
    <Head title="Local Supplier Skus" />
    <h1 class="mb-8 text-3xl font-bold">
    <Link class="text-orange-400 hover:text-orange-600" href="/localSuppliers">Local Suppliers</Link>
      <span class="text-orange-400 font-medium">/</span>SKUs: {{ supplierObj.name }}
    </h1>
    <div class="flex items-center justify-between mb-6 mt-6">
      <search-filter v-model="search.search" class="mr-4 w-full max-w-md" @reset="reset">
        <label class="block text-gray-700">Sku:</label>
      </search-filter>
      <Link class="btn-orange" :href="`/localSuppliers/${supplierObj.id}/supplierSkus/create`">
        <span>Add</span>
        <span class="hidden md:inline">&nbsp;SKU</span>
      </Link>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-3">Part Number</th>
          <th class="pb-3 pt-3 px-3">Part Description</th>
          <th class="pb-3 pt-3 px-3">Lead Time</th>
          <th class="pb-3 pt-3 px-3" colspan="2"></th>
        </tr>
        <tr v-for="supplier in suppliers.data" :key="supplier.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/localSuppliers/${supplier.supplier_id}/supplierSkus/${supplier.id}/edit`" tabindex="-1">
              {{ supplier.part_number }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/localSuppliers/${supplier.supplier_id}/supplierSkus/${supplier.id}/edit`" tabindex="-1">
              {{ supplier.description }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/localSuppliers/${supplier.supplier_id}/supplierSkus/${supplier.id}/edit`" tabindex="-1">
              {{ supplier.order_placed }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3" :href="`/localSuppliers/${supplier.supplier_id}/supplierSkus/${supplier.id}/edit`" tabindex="-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </Link>
          </td>
          <td class="border-t">
            <svg
              @click="openDeleteModal(supplier)"
              xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" style="color: red;" title="Delete">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </td>
        </tr>
        <tr v-if="suppliers.data.length === 0">
          <td class="px-3 py-3 border-t" colspan="4">No data found</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="suppliers.links" />
  </div>
  <!--modal content-->
  <div class="fixed z-10 inset-0 overflow-y-auto" v-if="isOpen">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <p>
                You are about to delete a supplier <strong>{{ supplierName }}</strong> with SKU <strong>{{ sku }}</strong>. Do you wish to continue?
              </p>
            </div>
          </div>
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
            <button type="button" @click="destroy(supplierId)" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-red-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-red-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red sm:text-sm sm:leading-5">
              Delete
            </button>
          </span>
          <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
            <button @click="closeModal" type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5">
              Cancel
            </button>
          </span>
        </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '@/Shared/Pagination'

export default {
  components: {
    Head,
    Link,
    Icon,
    TextInput,
    SelectInput,
    LoadingButton,
    SearchFilter,
    Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    suppliers: Object,
    supplierObj: Object,
  },
  data() {
    return {
      search: {
        search: this.filters.search,
      },
      isOpen: false,
      supplierName: null,
      supplierId: null,
      supplierSkuId: null,
      sku: null,
    }
  },
  watch: {
    search: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(`/localSuppliers/${this.supplierObj.id}/supplierSkus`, pickBy(this.search), {
          preserveState: true,
          preserveScroll: true,
          replace: true
        })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.search = mapValues(this.search, () => null)
    },
    openDeleteModal(data) {
      this.supplierName = data.name;
      this.supplierId = data.supplier_id;
      this.supplierSkuId = data.id;
      this.sku = data.part_number;
      this.isOpen = true;
    },
    closeModal() {
      this.supplierName = null;
      this.supplierId = null;
      this.isOpen = false;
    },
    destroy() {
      const data = {};
      data.supplier_id = this.supplierId;
      data.id = this.supplierSkuId;
      data._method = 'DELETE';
      this.$inertia.post(`/localSuppliers/${data.supplier_id}/supplierSkus/${data.id}`, data)
      this.supplierName = null;
      this.supplierId = null;
      this.supplierSkuId = null;
      this.closeModal();
    },
  },
}
</script>
